import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import type { RefSelectProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { CiSearch } from 'react-icons/ci';
import { LuCommand } from 'react-icons/lu';
import commandsList, {
  Command,
  normalizeCommandQuery,
} from '../commands';
import { useAdminPermissions } from '../hooks/use-permission-hook';
import { useCheckUserRole } from '../contexts/Utils';

const RECENT_KEY = 'ep_recent_commands_v2';
const MAX_RESULTS = 12;

type CommandOption = {
  label: React.ReactNode;
  title: string;
  value: string;
  command: Command;
};

const readRecentCommandIds = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveRecentCommand = (id: string) => {
  try {
    const current = readRecentCommandIds();
    localStorage.setItem(
      RECENT_KEY,
      JSON.stringify([id, ...current.filter((item) => item !== id)].slice(0, 8))
    );
  } catch {
    // localStorage can be unavailable in private or restricted browser contexts.
  }
};

const commandSearchText = (command: Command) =>
  normalizeCommandQuery(
    [
      command.title,
      command.description,
      command.category,
      command.route,
      ...command.keywords,
    ].join(' ')
  );

const getRankBoost = (command: Command, normalizedQuery: string) => {
  const title = normalizeCommandQuery(command.title);
  const category = normalizeCommandQuery(command.category);
  const keywords = command.keywords.map(normalizeCommandQuery);
  const tokens = normalizedQuery.split(' ').filter(Boolean);
  const searchable = commandSearchText(command);

  let boost = 0;
  if (title === normalizedQuery) boost += 0.55;
  if (keywords.some((keyword) => keyword === normalizedQuery)) boost += 0.5;
  if (title.startsWith(normalizedQuery)) boost += 0.35;
  if (title.includes(normalizedQuery)) boost += 0.25;
  if (category.includes(normalizedQuery)) boost += 0.12;

  const matchedTokens = tokens.filter((token) => searchable.includes(token));
  boost += tokens.length ? (matchedTokens.length / tokens.length) * 0.3 : 0;

  return boost;
};

const isCommandAllowed = (
  command: Command,
  permissions: ReturnType<typeof useAdminPermissions>,
  roleLevel: 'super' | 'company' | 'estate' | null
) => {
  if (command.roleLevels?.length && (!roleLevel || !command.roleLevels.includes(roleLevel))) {
    return false;
  }

  if (roleLevel === 'super') return true;
  if (!command.permissions?.length) return true;

  const knownPermissionKeys = Object.keys(permissions);
  if (!knownPermissionKeys.length) return false;

  const matchingPermissionKeys = command.permissions.filter((permission) =>
    knownPermissionKeys.includes(permission)
  );

  if (!matchingPermissionKeys.length) return false;

  return command.permissionMode === 'all'
    ? matchingPermissionKeys.every((permission) => Boolean(permissions[permission]))
    : matchingPermissionKeys.some((permission) => Boolean(permissions[permission]));
};

const resultLabel = (command: Command) => {
  const Icon = command.icon || CiSearch;

  return (
    <div className="flex w-full items-center gap-3 py-2">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-lit text-secondary">
        <Icon className="text-lg" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-dark">
          {command.title}
        </div>
        <div className="truncate text-xs text-label">
          {command.description}
        </div>
      </div>
      <div className="hidden shrink-0 text-xs text-gray md:block">
        {command.category}
      </div>
    </div>
  );
};

const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useAdminPermissions();
  const { isSuperAdminLevel, isCompanyLevel, isEstateLevel } = useCheckUserRole();
  const selectRef = useRef<RefSelectProps | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const roleLevel = isSuperAdminLevel()
    ? 'super'
    : isCompanyLevel()
      ? 'company'
      : isEstateLevel()
        ? 'estate'
        : null;

  const availableCommands = useMemo(() => {
    return commandsList.filter((command) =>
      isCommandAllowed(command, permissions, roleLevel)
    );
  }, [permissions, roleLevel]);

  const fuse = useMemo(() => {
    return new Fuse(availableCommands, {
      keys: [
        { name: 'title', weight: 0.45 },
        { name: 'keywords', weight: 0.35 },
        { name: 'description', weight: 0.15 },
        { name: 'category', weight: 0.05 },
      ],
      threshold: 0.45,
      distance: 120,
      ignoreLocation: true,
      minMatchCharLength: 2,
      includeScore: true,
    });
  }, [availableCommands]);

  const recentCommands = useMemo(() => {
    const ids = readRecentCommandIds();
    return ids
      .map((id) => availableCommands.find((command) => command.id === id))
      .filter(Boolean) as Command[];
  }, [availableCommands]);

  const results = useMemo(() => {
    const normalizedQuery = normalizeCommandQuery(query);

    if (!normalizedQuery) {
      const recentIds = new Set(recentCommands.map((command) => command.id));
      return [
        ...recentCommands,
        ...availableCommands.filter((command) => !recentIds.has(command.id)),
      ].slice(0, MAX_RESULTS);
    }

    const fuseResults = fuse.search(normalizedQuery);
    const scored = new Map<string, { command: Command; score: number }>();

    fuseResults.forEach((result) => {
      const baseScore = 1 - (result.score ?? 1);
      scored.set(result.item.id, {
        command: result.item,
        score: baseScore + getRankBoost(result.item, normalizedQuery),
      });
    });

    availableCommands.forEach((command) => {
      const searchable = commandSearchText(command);
      const queryTokens = normalizedQuery.split(' ').filter(Boolean);
      const hasEveryToken =
        queryTokens.length > 0 &&
        queryTokens.every((token) => searchable.includes(token));

      if (hasEveryToken || searchable.includes(normalizedQuery)) {
        const current = scored.get(command.id);
        const score = 0.65 + getRankBoost(command, normalizedQuery);
        if (!current || current.score < score) {
          scored.set(command.id, { command, score });
        }
      }
    });

    return Array.from(scored.values())
      .sort((first, second) => second.score - first.score)
      .map(({ command }) => command)
      .slice(0, MAX_RESULTS);
  }, [availableCommands, fuse, query, recentCommands]);

  const options = useMemo<CommandOption[]>(
    () =>
      results.map((command) => ({
        command,
        label: resultLabel(command),
        title: command.title,
        value: command.id,
      })),
    [results]
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const shouldOpen =
        (isMac && event.metaKey && event.key.toLowerCase() === 'k') ||
        (!isMac && event.ctrlKey && event.key.toLowerCase() === 'k');

      if (!shouldOpen) return;

      event.preventDefault();
      setOpen(true);
      setTimeout(() => selectRef.current?.focus(), 0);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const execute = (command: Command) => {
    saveRecentCommand(command.id);
    setOpen(false);
    setQuery('');
    selectRef.current?.blur();

    const url = new URL(command.route, window.location.origin);
    navigate(`${url.pathname}${url.search}`);
  };

  return (
    <div className="relative w-[260px] md:w-[360px]">
      <Select
        ref={selectRef}
        showSearch
        allowClear
        open={open}
        value={undefined}
        searchValue={query}
        options={options}
        filterOption={false}
        optionLabelProp="title"
        size="large"
        placeholder="Search pages, actions, reports"
        className="w-full"
        popupClassName="command-palette-dropdown"
        suffixIcon={
          <div className="flex items-center gap-1 text-xs text-gray">
            <LuCommand className="text-sm" />
            <span>K</span>
          </div>
        }
        notFoundContent={
          <div className="px-4 py-6 text-center text-sm text-label">
            No matching page, action, or report found
          </div>
        }
        onDropdownVisibleChange={setOpen}
        onSearch={setQuery}
        onClear={() => setQuery('')}
        onSelect={(_, option) => execute((option as CommandOption).command)}
        onClick={() => setOpen(true)}
      />
    </div>
  );
};

export default CommandPalette;
